import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { ListPagination } from '@/components/ListPagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Calendar, Search } from 'lucide-react';
import { format, getISOWeek } from 'date-fns';
import { businessWeekBounds, formatBusinessDate } from '@/lib/format';
import SectionDrawer from '@/components/navigation/SectionDrawer';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';

interface WeekContract {
    contract_id: number;
    vehicle_reg: string | null;
    total_paid: number;
    management_rate: number;
    partner_portion: number;
    payout_amount: number;
    status: string;
    vehicle_listing_url: string | null;
    vehicle_color: string | null;
    vehicle_updated_at: string | null;
    total_contracts: number;
    vehicle_make: string | null;
    vehicle_model: string | null;
    payout_date: string | null;
}

function getSbtPhotoUrl(listingUrl: string | null, updatedAt?: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

const formatCurrency = (amount: number) =>
    'GHS' + new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const PAGE_SIZE = 15;

export default function PayoutWeekDetail() {
    const navigate = useNavigate();
    const { partnerId, signOut } = useAuth();
    const [searchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const weekParam = searchParams.get('week');

    // `?week=` is a 'yyyy-MM-dd' Monday; absent, it means the current Ghana week.
    const { weekStart, weekEnd, fromInstant, toInstant } = businessWeekBounds(weekParam);
    const weekNumber = getISOWeek(weekStart);

    // Fetch contracts for this week with server-side pagination
    const { data: response, isLoading } = useQuery({
        queryKey: ['partner-week-contracts', partnerId, fromInstant, toInstant, currentPage, debouncedSearch],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_week_contracts', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                ...(debouncedSearch.trim() ? { p_search: debouncedSearch.trim() } : {}),
            });
            if (error) throw error;
            const rows = (data as WeekContract[]) || [];
            return {
                rows,
                totalItems: rows.length > 0 ? rows[0].total_contracts : 0,
            };
        },
        enabled: !!partnerId,
    });

    const contracts = response?.rows ?? [];
    const totalItems = response?.totalItems ?? 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'paid': return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
            case 'partial': return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>;
            case 'due': return <Badge className="bg-red-100 text-red-700 border-red-200">Due</Badge>;
            default: return <Badge variant="outline" className="text-muted-foreground">Upcoming</Badge>;
        }
    };

    const handleSignOut = async () => { await signOut(); navigate('/auth'); };
    const handleNavChange = (section: string) => { navigate(`/${section}`); };

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer activeSection="finance/payouts" onSectionChange={handleNavChange} onSignOut={handleSignOut} />
            </div>

            {/* Header */}
            <div>
                <Breadcrumbs items={[
                    { label: 'Payouts', href: '/finance/payouts' },
                    { label: `Week ${weekNumber}` },
                ]} />
                <h1 className="text-4xl font-semibold tracking-tight">
                    {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                </h1>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center">
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by vehicle..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                {totalPages > 1 && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            {/* Contracts Table */}
            {isLoading ? (
                <Skeleton className="h-48 w-full" />
            ) : contracts.length > 0 ? (
                <>
                    {/* Desktop table */}
                    <div className="hidden lg:block">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Vehicle</TableHead>
                                    <TableHead>Payout</TableHead>
                                    <TableHead>Paid On</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {contracts.map((c) => {
                                    const photoUrl = getSbtPhotoUrl(c.vehicle_listing_url, c.vehicle_updated_at);
                                    return (
                                    <TableRow key={c.contract_id}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                                {photoUrl ? (
                                                    <HoverCard openDelay={200} closeDelay={100}>
                                                        <HoverCardTrigger asChild>
                                                            <img
                                                                src={photoUrl}
                                                                alt=""
                                                                className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                                                            />
                                                        </HoverCardTrigger>
                                                        <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                                                            <img src={photoUrl} alt={c.vehicle_reg || ''} className="w-[480px] rounded-md" />
                                                        </HoverCardContent>
                                                    </HoverCard>
                                                ) : null}
                                                <div
                                                    className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                                                    style={{ backgroundColor: c.vehicle_color || '#e5e7eb' }}
                                                />
                                                <div>
                                                    <span className="font-medium">{c.vehicle_reg || `#${c.contract_id}`}</span>
                                                    {(c.vehicle_make || c.vehicle_model) && (
                                                        <p className="text-xs text-muted-foreground">{[c.vehicle_make, c.vehicle_model].filter(Boolean).join(' ')}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{formatCurrency(c.partner_portion)}</TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {c.payout_date ? formatBusinessDate(c.payout_date) : <span>—</span>}
                                        </TableCell>
                                        <TableCell>{getStatusBadge(c.status)}</TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Mobile cards */}
                    <div className="lg:hidden divide-y border rounded-lg">
                        {contracts.map((c) => {
                            const photoUrl = getSbtPhotoUrl(c.vehicle_listing_url, c.vehicle_updated_at);
                            return (
                            <div
                                key={c.contract_id}
                                className="p-4 space-y-2"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {photoUrl ? (
                                            <img
                                                src={photoUrl}
                                                alt=""
                                                className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                                            />
                                        ) : null}
                                        <div
                                            className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                                            style={{ backgroundColor: c.vehicle_color || '#e5e7eb' }}
                                        />
                                        <span className="font-medium text-sm">{c.vehicle_reg || `Contract #${c.contract_id}`}</span>
                                        {(c.vehicle_make || c.vehicle_model) && (
                                            <span className="text-xs text-muted-foreground">{[c.vehicle_make, c.vehicle_model].filter(Boolean).join(' ')}</span>
                                        )}
                                    </div>
                                    {getStatusBadge(c.status)}
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-xs text-muted-foreground">Payout</div>
                                        <div className="text-sm font-medium">{formatCurrency(c.partner_portion)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-muted-foreground">Paid On</div>
                                        <div className="text-sm">{c.payout_date ? formatBusinessDate(c.payout_date) : '—'}</div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <EmptyState
                    icon={<Calendar />}
                    title={debouncedSearch ? 'No results' : 'No repayments'}
                    description={debouncedSearch ? `No vehicles matching "${debouncedSearch}" were found for this week.` : 'No repayments were recorded for this week.'}
                />
            )}
        </div>
    );
}
